'use client'

import { useEffect } from 'react'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'

import { Dialog } from '@/components/atoms/Dialog'

type ModalViewProps = {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  children: React.ReactNode
  style?: 'wide' | 'info' | 'form'
  title?: string
  onDismiss?: () => void
}

export function Modal({
  isOpen,
  setIsOpen,
  children,
  style,
  title = '',
  onDismiss,
}: Readonly<ModalViewProps>) {
  const className = (() => {
    switch (style) {
      case 'wide':
        return 'h-[90%] !max-w-5xl !p-1 sm:!w-2/3'
      case 'info':
        return 'w-full md:max-w-2xl block'
      case 'form':
        return 'w-full md:max-w-2xl block'
      default:
        return ''
    }
  })()

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (!open && onDismiss) {
      onDismiss()
    }
  }

  useEffect(() => {
    if (typeof document === 'undefined') return

    if (!isOpen && document.body.style.pointerEvents === 'none') {
      document.body.style.pointerEvents = ''
    }

    return () => {
      if (document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = ''
      }
    }
  }, [isOpen])

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Content
        className={className}
        style={style}
        aria-describedby={undefined}
      >
        <VisuallyHidden asChild>
          <Dialog.Title>{title}</Dialog.Title>
        </VisuallyHidden>

        {children}
      </Dialog.Content>
    </Dialog.Root>
  )
}
